import { FC, useCallback, useState } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { Radio, Divider } from 'antd'
import { Formik, FormikErrors } from 'formik'
import { useAuth } from '../../../../context/auth-context'
import { useDateTime } from '../../../../shared/useDateTime'
import {
  EmployeeCloseMeetingInput,
  MeetingDashboardFragment,
  MeetingPdfStatus,
  MeetingStatus,
  ObjectiveAttachmentInfoDto,
} from '../../../../types'
import { getConclusionHrefByMeeting, getObjectiveAttachmentHref } from '../../../meeting/util/hrefUtils'
import { PerModal } from '../../../../components/custom_components/Modal'
import { Form, FormikTextArea, FormikRadioGroup } from '../../../../components/formik-component'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import PerText from '../../../../components/custom_components/Text'
import PerButtonCircle from '../../../../components/custom_components/ButtonCircle'
import PerSpacer from '../../../../components/custom_components/Spacer'
import { useObjectiveAttachmentHelper, atobUtf8 } from '../../../../shared/useObjectiveAttachmentHelper'

import PerIcon from '../../../../components/custom_components/Icon'

const Attachment: FC<{ objective: ObjectiveAttachmentInfoDto; employeeId: number }> = ({ objective, employeeId }) => {
  const { extractHumanReadableFileName } = useObjectiveAttachmentHelper(atobUtf8)

  return (
    <PerFlexRow align="middle" mb="xxs">
      <PerIcon name="attachment_fill" size={16} />
      {objective.attachment && objective.attachmentDownloadToken && (
        <>
          <PerText className="text-ellipsis" ml="xs" mr="xs">
            {extractHumanReadableFileName(objective.attachment)}
          </PerText>
          <PerButtonCircle
            color="primary"
            variant="bold"
            icon="view_fill"
            asLink={getObjectiveAttachmentHref(String(employeeId), objective.attachment, objective.attachmentDownloadToken)}
          />
        </>
      )}
    </PerFlexRow>
  )
}

const renderAttachments = (objectives: ObjectiveAttachmentInfoDto[], employeeId: number) =>
  objectives.map((obj, i) => <Attachment key={obj.globalId || i} objective={obj} employeeId={employeeId} />)

interface MeetingModalEmployeeProps {
  meeting: MeetingDashboardFragment
  objectivesWithAttachments: ObjectiveAttachmentInfoDto[]
  visible: boolean
  closeModal: () => void
  onCloseMeeting: (employeeCloseMeetingInput: EmployeeCloseMeetingInput) => void
  loading?: boolean
}

export const EmployeeCloseMeetingModal: FC<MeetingModalEmployeeProps> = ({
  visible,
  closeModal,
  meeting,
  onCloseMeeting,
  objectivesWithAttachments,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const [isSubmitting, toggleSubmitting] = useState<boolean>(false)
  const { formatDateShort } = useDateTime(user)

  const isPdfReady = useCallback(() => {
    return meeting.conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess
  }, [meeting.conclusionPdfStatus])

  const downloadHref = getConclusionHrefByMeeting(meeting)

  const renderDisputeAndConfirm = (values, dispute, confirm) => (values.approval === 'dispute' ? dispute : confirm)

  const isConfirmButtonDisabled = (errors: FormikErrors<{ approval: string | undefined; feedback: string | undefined }>): boolean => {
    return (!!errors.approval && errors.approval.length > 0) || (!!errors.feedback && errors.feedback.length > 0)
  }

  const createConfirmButtonTooltip = (props: { approval: string; feedback: string }): string | undefined => {
    if (props.approval === 'approval' || (props.approval === 'dispute' && props.feedback.trim().length >= 2)) {
      return undefined
    }
    if (props.approval === 'dispute') {
      if (props.feedback.trim().length === 0) {
        return t('appraisalMeeting.employeeCloseMeetingModal.confirmButtonTooltip.allFieldsRequired')
      }
      if (props.feedback.trim().length > 0 && props.feedback.trim().length < 2) {
        return t('appraisalMeeting.employeeCloseMeetingModal.confirmButtonTooltip.feedbackTooShort')
      }
    }
    if (props.approval === '') {
      return t('appraisalMeeting.employeeCloseMeetingModal.confirmButtonTooltip.genericMessage')
    }
    return undefined
  }

  return (
    <Formik
      initialValues={{ approval: '', feedback: '' }}
      onSubmit={(values, { resetForm }) => {
        toggleSubmitting(true)
        onCloseMeeting({
          id: meeting.id,
          employeeId: meeting.employee.id,
          feedbackForHR: values.feedback,
          meetingStatus: values.approval === 'approve' ? MeetingStatus.Confirmed : MeetingStatus.DisputedConfirmed,
        } as EmployeeCloseMeetingInput)
        resetForm()
        closeModal()
        toggleSubmitting(false)
      }}
      enableReinitialize={true}
      onReset={(values) => {
        values.approval = ''
        values.feedback = ''
        closeModal()
      }}
      validateOnMount
      validateOnChange={true}
      validateOnBlur={false}
      validationSchema={Yup.object().shape({
        approval: Yup.string().required(t('appraisalMeeting.employeeCloseMeetingModal.selectionRequired')),
        feedback: Yup.string().when(['approval'], {
          is: (approval) => approval === 'dispute',
          then: Yup.string()
            .required(t('appraisalMeeting.employeeCloseMeetingModal.feedbackRequired'))
            .trim()
            .min(2, t('appraisalMeeting.employeeCloseMeetingModal.feedbackTooShort')),
        }),
      })}
    >
      {(props) => {
        const { values, handleSubmit, handleReset, errors } = props

        return (
          <PerModal
            title={{
              heading: t('appraisalMeeting.employeeCloseMeetingModal.title'),
              subheading: t('appraisalMeeting.employeeCloseMeetingModal.meetingInfo', {
                date: formatDateShort(meeting.date),
                reviewer: meeting.reviewer && `${meeting.reviewer.firstName} ${meeting.reviewer.lastName}`,
              }),
            }}
            visible={visible}
            footer={{
              cancelBtn: { onClick: () => handleReset() },
              customBtns: [
                {
                  text: renderDisputeAndConfirm(
                    values,
                    t('appraisalMeeting.employeeCloseMeetingModal.dispute'),
                    t('appraisalMeeting.employeeCloseMeetingModal.confirm'),
                  ),
                  onClick: () => {
                    handleSubmit()
                  },
                  icon: renderDisputeAndConfirm(values, 'close_stroke', 'check_stroke'),
                  color: renderDisputeAndConfirm(values, 'error', 'success'),
                  disabled: isConfirmButtonDisabled(errors),
                  isProcessing: { active: isSubmitting },
                  type: 'submit',
                  tooltip: createConfirmButtonTooltip(values) || undefined,
                },
              ],
            }}
            onCancel={handleReset}
          >
            <PerText mb="l">{t('appraisalMeeting.employeeCloseMeetingModal.message')}</PerText>

            <PerFlexRow align="middle" mb="xxs">
              <PerIcon name="file_fill" size={16} />
              <PerText className="text-ellipsis" ml="xs" mr="xs">
                {meeting.conclusionPdfFileName}
              </PerText>
              <PerButtonCircle
                color="primary"
                variant="bold"
                icon="view_fill"
                asLink={downloadHref ? downloadHref : '#'}
                tooltip={
                  isPdfReady()
                    ? t('appraisalMeeting.employeeCloseMeetingModal.viewConclusionPdfTooltip.ready')
                    : t('appraisalMeeting.employeeCloseMeetingModal.viewConclusionPdfTooltip.notReady')
                }
              />
            </PerFlexRow>

            {renderAttachments(objectivesWithAttachments, meeting.employee.id)}

            <Form>
              <PerSpacer mt="l" mb="m">
                <FormikRadioGroup name="approval">
                  <Radio name="approval" value="approve">
                    {t('appraisalMeeting.employeeCloseMeetingModal.optionApprove')}
                  </Radio>
                  <PerSpacer mt={'xxs'} />
                  <Radio name="approval" value="dispute">
                    {t('appraisalMeeting.employeeCloseMeetingModal.optionDispute')}
                  </Radio>
                </FormikRadioGroup>
                {values.approval === 'dispute' && (
                  <PerText appearance="quote" color="error" mt="xxs" ml="l" fontSize="s">
                    {t('appraisalMeeting.employeeCloseMeetingModal.disputeInfo')}
                  </PerText>
                )}
              </PerSpacer>
              <Divider />

              <PerText mb={'xxs'}>
                {values.approval === 'dispute'
                  ? t('appraisalMeeting.employeeCloseMeetingModal.feedBackToHRCaseDispute')
                  : t('appraisalMeeting.employeeCloseMeetingModal.feedBackToHRCaseApproval')}
              </PerText>
              <FormikTextArea
                autoSize={{ minRows: 4, maxRows: 6 }}
                placeholder={t('appraisalMeeting.employeeCloseMeetingModal.placeholder')}
                name="feedback"
              />
            </Form>
          </PerModal>
        )
      }}
    </Formik>
  )
}
